import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import get from 'lodash.get';
import {
  Box,
  FlexBox,
  Typography,
  Button,
  SvgIcon,
  Grid,
  useMediaQuery,
  useTheme,
  color
} from '@kis-technologies/kis-ui';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { TextSlider } from 'components/TextSlider';
import { OPERATIONAL, CONNECTED, UNKNOWN, NO_PING } from 'constants/hardware';
import { useActions } from 'hooks/useActions';
import { actions as cartActions } from 'services/cart';
import { actions as cityPassActions } from 'services/cityPass';
import { selectors as configSelectors, LanguageField, Configuration } from 'services/configuration';
import { actions as customerActions } from 'services/customer';
import { selectors as hardwareSelectors, actions as hardwareActions } from 'services/hardware';
import { selectors as kioskSelectors, actions as kioskActions } from 'services/kiosk';
import { actions as mediaActions, selectors as mediaSelectors } from 'services/media';
import { selectors as ticketingSelectors, actions as ticketingActions } from 'services/ticketing';
import { actions as salesErrorActions } from 'services/salesErrors';
import { getConfiguredOrTranslationValues, readTranslationFiles } from 'utils/helpers';
import packageJSON from '../../../../package.json';
import { isCurrentTimeInRange } from '../utils';
import { useStyles, AttractLoopImage } from './styled';
import Slider from 'react-slick';

const AttractLoopView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const themes = useTheme();
  const matchPortrait = useMediaQuery(themes.breakpoints.down('lg'));
  const navigate = useNavigate();
  // Reducer Selector
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const kioskVersion = useSelector(kioskSelectors.selectKioskVersion);
  const loading = useSelector(ticketingSelectors.selectLoading);
  const payment = useSelector(hardwareSelectors.selectPaymentDevice);
  const hardwarePrinters = useSelector(hardwareSelectors.selectPrinters);
  const scanner = useSelector(hardwareSelectors.selectScanner);
  const printersOperational = hardwarePrinters.every((p) => [OPERATIONAL, CONNECTED, UNKNOWN].includes(p.status.code));
  const paymentOperational = [OPERATIONAL, NO_PING, UNKNOWN].includes(payment.status.code);
  const scannerOperational = [OPERATIONAL, UNKNOWN].includes(scanner.status.code);
  const kioskPayment = useSelector(kioskSelectors.selectPaymentHardware);
  const kioskScanner = useSelector(kioskSelectors.selectScannerHardware);
  const kiosk = useSelector(kioskSelectors.selectKiosk);
  const kioskLoading = useSelector(kioskSelectors.selectLoading);
  const attractImages = useSelector(mediaSelectors.selectAttractImages) || [];
  const attractVideo = useSelector(mediaSelectors.selectAttractVideo) || [];
  const kioskPendingStatus = useSelector(kioskSelectors.selectKioskWaiting);
  const rebootPending = useSelector(kioskSelectors.selectPendingKioskReboot);

  // Gets these key values from the configuration object using lodash.get
  const attractLoop = get(configuration, 'configuration.globalSettings.attractLoop');
  const imagesOrder = get(attractLoop, 'order', []) as string[];
  const name = get(attractLoop, `name.${i18n.language}`, '') as string;
  const mediaType = get(attractLoop, 'type', '') as string;
  const transitionTime = get(attractLoop, 'transitionTime', 5) as number;
  const linkedContentUrl = get(attractLoop, 'linkedContentUrl', '') as string;
  const attractLoopHeader = get(attractLoop, `messages.header.${i18n.language}`);
  const attractLoopMessage = get(attractLoop, `messages.message.${i18n.language}`);
  const languageTransitionTime = get(attractLoop, 'languageTransitionTime');
  const fontColor = get(attractLoop, 'fontColor', '');
  const disablePingRanges: string[] = get(kiosk, 'configuration.disableHardwarePing', []);
  const flowTemplate = get(configuration, 'configuration.flowTemplate') as string;
  const palette = get(configuration, 'configuration.globalSettings.palette');
  const showSplashScreen = get(configuration, 'configuration.globalSettings.splashPage.enabled');
  const languageSettings: LanguageField[] = get(configuration, 'configuration.languageSettings', []);
  const defaultLanguage = languageSettings.find((item) => item.isDefault);

  const clientId = get(kiosk, 'venue.clientId');
  const previewUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/`;
  const classes = useStyles({ palette, fontColor })();

  if (attractImages && attractImages.length) {
    attractImages.forEach((item) => {
      if (imagesOrder) {
        let priority = imagesOrder.indexOf(item.s3Name);
        if (priority === -1) priority = imagesOrder.length;
        item.priority = priority;
      }
    });
    attractImages.sort((a, b) => a.priority - b.priority);
  }

  // Sets the index of the image from images array
  const [imageIndex, setImageIndex] = useState(0);
  const [hardwareError, setHardwareError] = useState('');
  const [uiLoading, setUILoading] = useState(false);
  const [configuredLangTranslations, setConfiguredLangTranslations] = useState();

  const {
    resetSession,
    resetCart,
    resetCustomer,
    resetTicketing,
    resetTickets,
    setError,
    checkVersion,
    reloadApplication,
    getMedia,
    updateKiosk,
    kioskStatusWaiting,
    kioskRebootPending,
    setStatusActor,
    resetCityPass
  } = useActions({
    resetSession: ticketingActions.resetSession,
    resetCart: cartActions.resetCart,
    resetCustomer: customerActions.resetCustomer,
    resetTicketing: ticketingActions.resetTicketing,
    resetTickets: kioskActions.resetTickets,
    setError: salesErrorActions.setError,
    checkVersion: kioskActions.checkVersion,
    reloadApplication: kioskActions.reloadApplication,
    getMedia: mediaActions.getMedia,
    updateKiosk: kioskActions.updateKiosk,
    kioskStatusWaiting: kioskActions.kioskStatusWaiting,
    kioskRebootPending: kioskActions.kioskRebootPending,
    setStatusActor: kioskActions.setStatusActor,
    resetCityPass: cityPassActions.resetCityPass
  });

  const { pingPrinters, paymentPing, scannerPing, setPrintData, resetSaleData } = useActions(hardwareActions);

  useEffect(() => {
    if (kioskPendingStatus) {
      updateKiosk({ status: 'Inactive' });
      setStatusActor('kcc');
      kioskStatusWaiting();
    }
    if (rebootPending) {
      kioskRebootPending();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /*
    // Whenever there is a change to images or image index
    // and images array has length, set timeout of 5 sec
    */
    let timeoutId: ReturnType<typeof setTimeout>;
    if (attractImages.length) {
      const nextIndex = (imageIndex + 1) % attractImages.length;
      timeoutId = setTimeout(() => setImageIndex(nextIndex), transitionTime * 1000);
    }
    // on unmount clear the timeout
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attractImages, imageIndex]);

  useEffect(() => {
    getMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration]);

  useEffect(() => {
    // This is to reset the session on every flow
    resetSession();
    resetTicketing();
    resetTickets();
    resetCustomer();
    resetCart();
    resetSaleData();
    resetCityPass();
    setPrintData({ printJobs: [] });
    checkVersion();
    i18n.changeLanguage(defaultLanguage && defaultLanguage.langCode);

    // Ping hardware every 30 seconds for any errors
    const interval = setInterval(() => {
      pingHardwares();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!kioskLoading) {
      // If hardware status is unknown, trigger the ping immediately
      if (
        (kioskScanner && scanner.status.code === UNKNOWN) ||
        (kioskPayment && payment.status.code === UNKNOWN) ||
        (hardwarePrinters.length && hardwarePrinters.some((p) => p.status.code === UNKNOWN))
      ) {
        pingHardwares();
      }
    }
  }, [kioskLoading]);

  useEffect(() => {
    // Get translations for the text slider
    const getData = async () => {
      const languages = languageSettings.map((lang) => lang.langCode);
      const translationStrings: any = await readTranslationFiles(languages);
      for (const lang of languages) {
        i18n.addResourceBundle(lang, 'translation', translationStrings[lang]);
      }
      setConfiguredLangTranslations(translationStrings);
    };

    if (languageSettings.length && !configuredLangTranslations) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuredLangTranslations]);

  useEffect(() => {
    if (hardwareError) {
      setError({ type: hardwareError });
      navigate('/ticketing/attract');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hardwareError]);

  useEffect(() => {
    if (!loading) {
      if (hardwarePrinters.length && !printersOperational) {
        setHardwareError('printer');
        setStatusActor('printerError');
      } else if (kioskPayment && !paymentOperational) {
        setHardwareError('payment device');
        setStatusActor('paymentDeviceError');
      } else if (kioskScanner && !scannerOperational) {
        setHardwareError('barcode scanner');
        setStatusActor('scannerError');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printersOperational, paymentOperational, scannerOperational]);

  // Handle the kiosk version change
  useEffect(() => {
    if (packageJSON.version && kioskVersion && packageJSON.version !== kioskVersion) {
      setTimeout(reloadApplication, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskVersion]);

  const pingHardwares = () => {
    if (isValidPingTimeRange()) {
      if (hardwarePrinters.length) pingPrinters({});
      if (kioskPayment) paymentPing();
      if (kioskScanner) scannerPing();
    }
  };

  const isValidPingTimeRange = () => {
    let isValid = true;
    if (disablePingRanges && disablePingRanges.length) {
      isValid = !isCurrentTimeInRange(disablePingRanges);
    }

    return isValid;
  };

  const isLanguageButtonsSliderEnabled = () => {
    let enabled = false;
    if (languageSettings.length) {
      if (matchPortrait && languageSettings.length > 3) enabled = true;
      else if (!matchPortrait && languageSettings.length > 6) enabled = true;
    }

    return enabled;
  };

  const CustomNextArrow = (arrowProps: any) => {
    const { className, style, onClick } = arrowProps;
    return (
      <div className={className} style={{ ...style, display: 'block' }} onClick={onClick}>
        <SvgIcon name="next_circle" color={color.DARK_GRAY} size={50} />
      </div>
    );
  };

  const CustomPrevArrow = (arrowProps: any) => {
    const { className, style, onClick } = arrowProps;
    return (
      <div className={className} style={{ ...style, display: 'block' }} onClick={onClick}>
        <SvgIcon name="prev_circle" color={color.DARK_GRAY} size={50} />
      </div>
    );
  };

  // to check the ticketing flow
  const navigateToNextPage = () => {
    setUILoading(false);
    if (showSplashScreen) {
      navigate('/ticketing/splash');
    } else {
      navigate('/ticketing/start-purchase');
    }
  };

  // used for types attract-video and attract-url
  const AbsoluteAttractText = useCallback(
    () => (
      <FlexBox fullWidth fullHeight position="absolute" direction="column" align="center" justify="center">
        {/* Transition through each language if configured */}
        {languageSettings.length && languageTransitionTime ? (
          <Typography
            fontSize={64}
            fontWeight={900}
            color={fontColor || color.WHITE}
            align="center"
            sx={{ whiteSpace: 'pre-wrap' }}
          >
            <TextSlider
              height={'0px'}
              width={'825px'}
              items={getConfiguredOrTranslationValues(
                languageSettings,
                i18n,
                'attract.welcomeTo',
                'attract.touchToBegin',
                attractLoop,
                'messages.header',
                'messages.message'
              )}
              name={name}
              transitionTime={languageTransitionTime}
            />{' '}
            <br /> <br /> <br />
          </Typography>
        ) : (
          <>
            {/* Or just show the header/message for the one language */}
            <Typography
              fontSize={64}
              fontWeight={900}
              color={fontColor || color.WHITE}
              align="center"
              sx={{ whiteSpace: 'pre-wrap' }}
            >
              {attractLoopHeader ? (
                attractLoopHeader
              ) : (
                <>
                  {t('attract.welcomeTo')}
                  <br />
                  {name}
                </>
              )}
            </Typography>
            <Box height="30px" flexOverride="none" />
            <Typography fontSize={50} fontWeight={800} color={fontColor || color.WHITE} sx={{ whiteSpace: 'pre-wrap' }}>
              {attractLoopMessage ? attractLoopMessage : t('attract.touchToBegin')}
            </Typography>
          </>
        )}
      </FlexBox>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, attractLoopHeader, attractLoopMessage]
  );

  const languageSliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 6,
    nextArrow: <CustomNextArrow />,
    prevArrow: <CustomPrevArrow />,
    responsive: [
      {
        breakpoint: 1920,
        settings: {
          slidesToShow: 6,
          slidesToScroll: 6,
          infinite: true,
          dots: false,
          nextArrow: <CustomNextArrow />,
          prevArrow: <CustomPrevArrow />
        }
      },
      {
        breakpoint: 1080,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          infinite: true,
          dots: false,
          nextArrow: <CustomNextArrow />,
          prevArrow: <CustomPrevArrow />
        }
      }
    ]
  };

  if (uiLoading || loading) return <LoadingOverlay style={{ zIndex: '99' }} />;

  return (
    <>
      {flowTemplate !== 'fair' && (
        <Grid container spacing={0} className={classes.attractContainer}>
          <Grid container className={classes.attractInnerContainer} onClick={navigateToNextPage}>
            {/* If no media configured for attract loop page */}
            {(!mediaType ||
              (mediaType === 'attract-video' && attractVideo.length === 0) ||
              (mediaType === 'attract-image' && attractImages.length === 0) ||
              (mediaType === 'attract-url' && !linkedContentUrl)) && (
              <FlexBox fullWidth fullHeight position="absolute" direction="column" align="center" justify="center">
                {/* Transition through each language if configured */}
                {languageSettings.length && languageTransitionTime ? (
                  <Typography
                    fontSize={64}
                    fontWeight={900}
                    align="center"
                    sx={{ whiteSpace: 'pre-wrap' }}
                    color={fontColor}
                  >
                    <TextSlider
                      height={'0px'}
                      width={'825px'}
                      items={getConfiguredOrTranslationValues(
                        languageSettings,
                        i18n,
                        'attract.welcomeTo',
                        'attract.touchToBegin',
                        attractLoop,
                        'messages.header',
                        'messages.message'
                      )}
                      name={name}
                      transitionTime={languageTransitionTime}
                    />{' '}
                    <br /> <br />
                  </Typography>
                ) : (
                  <>
                    {/* Or just show the header/message for the one language */}
                    <Typography
                      fontSize={64}
                      fontWeight={900}
                      align="center"
                      sx={{ whiteSpace: 'pre-wrap' }}
                      color={fontColor}
                    >
                      {attractLoopHeader ? (
                        attractLoopHeader
                      ) : (
                        <>
                          {t('attract.welcomeTo')}
                          <br />
                          {name}
                        </>
                      )}
                    </Typography>
                    <Box height="30px" flexOverride="none" />
                    <Typography fontSize={50} fontWeight={800} sx={{ whiteSpace: 'pre-wrap' }} color={fontColor}>
                      {attractLoopMessage ? attractLoopMessage : t('attract.touchToBegin')}
                    </Typography>
                  </>
                )}
              </FlexBox>
            )}
            {/* If there is a video render the video with the header and message */}
            {mediaType === 'attract-video' && attractVideo.length > 0 && (
              <>
                <video data-testid="attract-loop-video" width="100%" autoPlay muted loop style={{ objectFit: 'fill' }}>
                  <source src={`${previewUrl}${attractVideo[0].s3Name}`} />
                </video>
                <AbsoluteAttractText />
              </>
            )}

            {/* If there are images render the image with the header and message */}
            {mediaType === 'attract-image' && attractImages.length > 0 && attractImages[imageIndex] && (
              <>
                <Grid item md={12} lg={6} xl={6} className={classes.attractImageBlock}>
                  <FlexBox bg="x-light-gray">
                    <AttractLoopImage
                      alt="attract-loop-image"
                      style={{ height: '880px' }}
                      src={`${previewUrl}${attractImages[imageIndex].s3Name}`}
                    />
                  </FlexBox>
                </Grid>
                <Grid item md={12} lg={6} xl={6} className={classes.slideshowTextBlock}>
                  <FlexBox justify="center" direction="column" padding="5 10">
                    {/* Transition through each language if configured */}
                    {languageSettings.length && languageTransitionTime ? (
                      <Typography fontSize={64} fontWeight={900} sx={{ whiteSpace: 'pre-wrap' }}>
                        <TextSlider
                          height={'auto'}
                          width={'700px'}
                          items={getConfiguredOrTranslationValues(
                            languageSettings,
                            i18n,
                            'attract.welcomeTo',
                            'attract.touchToBegin',
                            attractLoop,
                            'messages.header',
                            'messages.message'
                          )}
                          name={name}
                          transitionTime={languageTransitionTime}
                        />
                      </Typography>
                    ) : (
                      <>
                        {/* Or just show the header/message for the one language */}
                        <Typography fontSize={64} fontWeight={900} sx={{ whiteSpace: 'pre-wrap' }}>
                          {attractLoopHeader ? (
                            attractLoopHeader
                          ) : (
                            <>
                              {t('attract.welcomeTo')}
                              <br />
                              {name}
                            </>
                          )}
                        </Typography>
                        <Box height="30px" flexOverride="none" />
                        <Typography fontSize={50} fontWeight={800} sx={{ whiteSpace: 'pre-wrap' }}>
                          {attractLoopMessage ? attractLoopMessage : t('attract.touchToBegin')}
                        </Typography>
                      </>
                    )}
                  </FlexBox>
                </Grid>
              </>
            )}

            {/* If the type is content url, show it inside an iframe */}
            {mediaType === 'attract-url' && linkedContentUrl && (
              <>
                <iframe
                  title="Attract Loop Linked Content"
                  src={linkedContentUrl}
                  frameBorder="0"
                  width="100%"
                  height="100%"
                  allowFullScreen
                >
                  Iframe Not supported
                </iframe>
                <AbsoluteAttractText />
              </>
            )}
          </Grid>
          {languageSettings.length > 1 && (
            <Grid className={classes.languageButtonsContainer}>
              {isLanguageButtonsSliderEnabled() ? (
                <div style={{ width: !matchPortrait ? '1620px' : '780px', height: '100%', textAlign: 'center' }}>
                  <Slider {...languageSliderSettings}>
                    {languageSettings.map((language, index) => (
                      <Button
                        key={index}
                        customSize="x-large"
                        className={
                          i18n.language === language.langCode ? classes.buttonSelected : classes.buttonNotSelected
                        }
                        onClick={(_e) => {
                          setUILoading(true);
                          i18n.changeLanguage(language.langCode, () => navigateToNextPage());
                        }}
                      >
                        {language.displayText}
                      </Button>
                    ))}
                  </Slider>
                </div>
              ) : (
                languageSettings.map((language, index) => (
                  <Button
                    key={index}
                    customSize="x-large"
                    className={i18n.language === language.langCode ? classes.buttonSelected : classes.buttonNotSelected}
                    onClick={(_e) => {
                      setUILoading(true);
                      i18n.changeLanguage(language.langCode, () => navigateToNextPage());
                    }}
                  >
                    {language.displayText}
                  </Button>
                ))
              )}
            </Grid>
          )}
        </Grid>
      )}
      {flowTemplate === 'fair' && attractImages.length > 0 && (
        <FlexBox height="1920px" overflow="hidden" onClick={() => navigate('/ticketing/start-purchase')}>
          <AttractLoopImage
            alt="attract-loop-image"
            style={{ height: '100%', width: '100%' }}
            src={`${previewUrl}${attractImages[imageIndex].s3Name}`}
          />
        </FlexBox>
      )}
    </>
  );
};

export default AttractLoopView;
